/* Canvello v8 — search (Task 4.4). Replaces Dawn's predictive-search.js.
 *
 * <cv-search> wraps any search form (the header's search panel, the drawer,
 * the collection and search tool bars). Every form works without this file:
 * it is a GET to /search. With it:
 *
 *   Suggestions  typing (2+ characters, after a 200ms pause) asks Shopify's
 *                predictive search for sections/cv-predictive.liquid and
 *                shows it under the field: suggested searches, collections,
 *                pieces, "See all results". Answers are kept for the page's
 *                life; a slower answer to an older query is dropped.
 *   Keyboard     the ARIA 1.2 combobox: focus stays in the field, ↓/↑ move
 *                through the options (aria-activedescendant), Enter follows
 *                the active one or submits the search, Escape closes the
 *                list (a second Escape is left to the panel or drawer
 *                around it), Tab closes it and moves on. The number of
 *                suggestions is spoken politely.
 *   Languages    where Shopify's predictive search refuses the language
 *                (Arabic), the same section comes from the search route,
 *                rendered from the search results: pieces only.
 *   Scope        the theme setting "Search covers" (pillow site / whole
 *                shop) is applied on the server, in the section.
 *
 * <cv-find> is the header's search panel: the Search link (a link to /search
 * without JS) becomes its button. It opens under the header with focus in
 * the field; Escape, a click outside, the button again or focus leaving it
 * close it, and focus returns to the button when it was inside.
 */
(() => {
  if (customElements.get('cv-search')) return;

  const MIN = 2;
  const cache = new Map();
  // Empty field (2026-10-07): popular searches from the section, recent ones
  // from this browser only (never sent anywhere).
  const EMPTY = '\u0000empty';
  const RECENT = 'cv-recent-searches';
  const KEEP = 5;
  const recent = {
    get() {
      try {
        const v = JSON.parse(localStorage.getItem(RECENT) || '[]');
        return Array.isArray(v) ? v.filter((t) => typeof t === 'string' && t.trim()).slice(0, KEEP) : [];
      } catch (e) {
        return [];
      }
    },
    add(term) {
      const t = String(term || '').trim().slice(0, 80);
      if (t.length < MIN) return;
      const list = [t, ...recent.get().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, KEEP);
      try {
        localStorage.setItem(RECENT, JSON.stringify(list));
      } catch (e) {
        /* private mode or storage off: nothing kept */
      }
    },
    clear() {
      try {
        localStorage.removeItem(RECENT);
      } catch (e) {
        /* nothing to clear */
      }
    },
  };
  let uid = 0;
  // Set once predictive search has refused this page's language.
  let viaSearch = false;
  const base = () => (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CVSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input[name="q"]');
      this.form = this.input && this.input.form;
      this.box = this.querySelector('[data-cv-ps-box]');
      this.live = this.querySelector('[data-cv-ps-live]');
      if (!this.input || !this.box) return;
      this.uid = `cvS${++uid}`;
      this.box.id = this.box.id || `${this.uid}-box`;
      this.active = -1;
      this.timer = 0;
      this.ctrl = null;
      this.shownFor = '';

      const i = this.input;
      i.setAttribute('role', 'combobox');
      i.setAttribute('aria-autocomplete', 'list');
      i.setAttribute('aria-expanded', 'false');
      i.setAttribute('aria-controls', this.box.id);
      i.setAttribute('autocomplete', 'off');

      i.addEventListener('input', () => this.onInput());
      i.addEventListener('keydown', (e) => this.onKey(e));
      i.addEventListener('focus', () => {
        const q = i.value.trim();
        if (q.length < MIN) this.empty();
        else if (this.shownFor && this.shownFor === q) this.open();
      });
      // A press on an option must not take focus from the field first.
      this.box.addEventListener('mousedown', (e) => e.preventDefault());
      this.box.addEventListener('click', (e) => {
        if (e.target.closest('[data-cv-ps-clear]')) {
          recent.clear();
          this.empty();
          return;
        }
        const link = e.target.closest('[data-cv-ps-link]');
        if (!link) return;
        // A search followed from the list is remembered like a typed one.
        const opt = link.closest('.cv-ps__opt--query, .cv-ps__opt--all');
        if (opt) recent.add(link.dataset.cvPsTerm || (link.hasAttribute('data-cv-ps-all') ? i.value : link.textContent));
        this.close();
      });
      this.addEventListener('focusout', (e) => {
        if (!e.relatedTarget || !this.contains(e.relatedTarget)) this.close();
      });
      this.onDoc = (e) => !this.contains(e.target) && this.close();
      document.addEventListener('pointerdown', this.onDoc);
      if (this.form)
        this.form.addEventListener('submit', () => {
          recent.add(i.value);
          this.close();
        });
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDoc);
    }

    onInput() {
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < MIN) {
        if (this.ctrl) this.ctrl.abort();
        this.close();
        this.box.innerHTML = '';
        this.shownFor = '';
        if (document.activeElement === this.input) this.empty();
        return;
      }
      this.timer = setTimeout(() => this.fetch(q), 200);
    }

    // The empty field: recent and popular searches, fetched once per page
    // from the search route without a query (the section's empty branch).
    async empty() {
      let html = cache.get(EMPTY);
      if (html === undefined) {
        try {
          const action = (this.form && this.form.getAttribute('action')) || '/search';
          const r = await fetch(`${action}?section_id=cv-predictive`);
          if (!r.ok) throw new Error(String(r.status));
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          const part = doc.getElementById('cv-predictive');
          html = part ? part.innerHTML : '';
        } catch (err) {
          html = '';
        }
        cache.set(EMPTY, html);
      }
      if (!html || this.input.value.trim().length >= MIN || document.activeElement !== this.input) return;
      this.render(html, '');
      const list = this.box.querySelector('[role="listbox"]');
      const group = this.box.querySelector('[data-cv-ps-recent]');
      if (list && group) {
        const terms = recent.get();
        const scope = list.getAttribute('data-cv-ps-scope') || '';
        const url = list.getAttribute('data-cv-ps-url') || '/search';
        const opts = group.querySelector('[role="group"]');
        terms.forEach((t, n) => {
          const li = document.createElement('li');
          li.className = 'cv-ps__opt cv-ps__opt--query';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.id = `${this.uid}-cvPsE-r${n}`;
          const a = document.createElement('a');
          a.className = 'cv-ps__link';
          a.href = `${url}?q=${encodeURIComponent(t)}${scope}`;
          a.tabIndex = -1;
          a.setAttribute('data-cv-ps-link', '');
          a.dataset.cvPsTerm = t;
          const s = document.createElement('span');
          s.className = 'cv-ps__q';
          s.textContent = t;
          a.append(s);
          li.append(a);
          opts.append(li);
        });
        group.hidden = terms.length === 0;
      }
      // Nothing to offer (no history, no popular searches): stay closed.
      if (!this.options().length) this.close();
    }

    async fetch(q) {
      let html = cache.get(q);
      if (!html) {
        if (this.ctrl) this.ctrl.abort();
        const ctrl = (this.ctrl = new AbortController());
        const params = new URLSearchParams({
          q,
          'resources[type]': 'query,collection,product',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each',
          // Suggestions search titles only unless asked: descriptions (body),
          // tags and SKUs too, as the search page does (2026-10-17).
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,variants.sku,body',
          'resources[options][prefix]': 'last',
          section_id: 'cv-predictive',
        });
        this.setAttribute('aria-busy', 'true');
        try {
          let r = viaSearch ? null : await fetch(`${base()}?${params}`, { signal: ctrl.signal });
          // Predictive search refuses some languages (Arabic: 417 "Unsupported
          // buyer locale"): ask the search route for the same section, which
          // then renders from the search results (pieces only).
          if (!r || r.status === 417) {
            viaSearch = true;
            const p2 = new URLSearchParams(this.form ? new FormData(this.form) : undefined);
            p2.set('q', q);
            p2.set('section_id', 'cv-predictive');
            const action = (this.form && this.form.getAttribute('action')) || '/search';
            r = await fetch(`${action}?${p2}`, { signal: ctrl.signal });
          }
          if (!r.ok) throw new Error(String(r.status));
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          const part = doc.getElementById('cv-predictive');
          if (!part) throw new Error('no section');
          html = part.innerHTML;
          cache.set(q, html);
        } catch (err) {
          // Offline, rate-limited or aborted: the form still submits.
          return;
        } finally {
          if (this.ctrl === ctrl) {
            this.ctrl = null;
            this.removeAttribute('aria-busy');
          }
        }
      }
      // The field moved on while this was in flight.
      if (this.input.value.trim() !== q) return;
      this.render(html, q);
    }

    render(html, q) {
      this.box.innerHTML = html;
      // Several search fields share a page: make every id unique.
      this.box.querySelectorAll('[id]').forEach((el) => (el.id = `${this.uid}-${el.id}`));
      this.box.querySelectorAll('[aria-labelledby]').forEach((el) => {
        el.setAttribute('aria-labelledby', el.getAttribute('aria-labelledby').split(' ').map((id) => `${this.uid}-${id}`).join(' '));
      });
      const list = this.box.querySelector('[role="listbox"]');
      if (list) this.input.setAttribute('aria-controls', list.id);
      this.shownFor = q;
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (document.activeElement === this.input) this.open();
      const status = this.box.querySelector('[data-cv-ps-status]');
      if (this.live && status) {
        this.live.textContent = '';
        requestAnimationFrame(() => (this.live.textContent = status.textContent.trim()));
      }
      if (window.CVMotion) window.CVMotion.scan(this.box);
    }

    options() {
      return Array.from(this.box.querySelectorAll('[role="option"]'));
    }

    get isOpen() {
      return !this.box.hidden && this.input.getAttribute('aria-expanded') === 'true';
    }

    open() {
      if (!this.box.querySelector('[role="listbox"]')) return;
      this.box.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('data-open', '');
    }

    close() {
      this.box.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.removeAttribute('data-open');
      this.setActive(-1);
    }

    setActive(i) {
      const opts = this.options();
      opts.forEach((o, n) => o.setAttribute('aria-selected', String(n === i)));
      this.active = i;
      if (i < 0 || !opts[i]) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      this.input.setAttribute('aria-activedescendant', opts[i].id);
      opts[i].scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
    }

    onKey(e) {
      const opts = this.options();
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!opts.length) return;
          e.preventDefault();
          if (!this.isOpen) {
            this.open();
            this.setActive(e.key === 'ArrowDown' ? 0 : opts.length - 1);
            return;
          }
          const step = e.key === 'ArrowDown' ? 1 : -1;
          const next = this.active === -1 ? (step > 0 ? 0 : opts.length - 1) : (this.active + step + opts.length) % opts.length;
          this.setActive(next);
          break;
        }
        case 'Enter': {
          if (!this.isOpen || this.active < 0) return; // the form submits
          const link = opts[this.active] && opts[this.active].querySelector('a[href]');
          if (!link) return;
          e.preventDefault();
          this.close();
          link.click();
          break;
        }
        case 'Escape':
          if (!this.isOpen) return; // left to the panel or drawer around it
          e.preventDefault();
          e.stopPropagation();
          this.close();
          break;
        case 'Tab':
          this.close();
          break;
        default:
      }
    }
  }

  customElements.define('cv-search', CVSearch);

  class CVFind extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.opener = document.querySelector(`[data-cv-find-open="${CSS.escape(this.id)}"]`);
      this.input = this.querySelector('input[name="q"]');
      if (!this.opener || !this.input) return;
      const o = this.opener;
      o.setAttribute('role', 'button');
      o.setAttribute('aria-expanded', 'false');
      o.setAttribute('aria-controls', this.id);
      o.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        this.hidden ? this.show() : this.hide(true);
      });
      // A link turned button: Space presses it too.
      o.addEventListener('keydown', (e) => {
        if (e.key !== ' ') return;
        e.preventDefault();
        o.click();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || this.hidden) return;
        e.preventDefault();
        this.hide(true);
      });
      this.addEventListener('focusout', (e) => {
        const to = e.relatedTarget;
        if (to && !this.contains(to) && to !== o) this.hide(false);
      });
      this.onDoc = (e) => !this.hidden && !this.contains(e.target) && !o.contains(e.target) && this.hide(false);
      document.addEventListener('pointerdown', this.onDoc);
      // "/" opens search from anywhere but a field (the hint on the button).
      this.onSlash = (e) => {
        if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
        const t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        if (!o.getClientRects().length) return; // the header's button is not shown (offsetParent is null in a fixed header)
        e.preventDefault();
        if (this.hidden) this.show();
        else this.input.focus();
      };
      document.addEventListener('keydown', this.onSlash);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDoc);
      document.removeEventListener('keydown', this.onSlash);
    }

    show() {
      this.hidden = false;
      this.opener.setAttribute('aria-expanded', 'true');
      document.documentElement.setAttribute('data-cv-finding', '');
      this.input.focus();
      this.input.select();
    }

    hide(refocus) {
      if (this.hidden) return;
      const inside = this.contains(document.activeElement);
      this.hidden = true;
      this.opener.setAttribute('aria-expanded', 'false');
      document.documentElement.removeAttribute('data-cv-finding');
      if (refocus || inside) this.opener.focus();
    }
  }

  customElements.define('cv-find', CVFind);
})();
